/**
 * Accueil formateur — refonte 2026-09-02 (correction v2).
 *
 * Ordre (comme demandé) :
 *  1. Header sticky
 *  2. Alerte cours imminent (si présent)
 *  3. Composer rapide
 *  4. Raccourcis EN HAUT : Enseignement / Communication / Ressources
 *     (Le bouton "Statistiques" est dans Enseignement, ouvre la page dédiée.)
 *  5. Chips "Mes classes"
 *  6. Fil d'actualités (dans le fil, les publications récentes sont en haut,
 *     c'est le comportement natif du backend : tri par date_publication desc.)
 *
 * Palette slate/blue (fond `bg-slate-50`, cartes blanches, icônes lucide
 * simples, sans emoji, sans gradients). Le tableau de bord n'est PLUS pliable
 * ici — il vit dans `/formateur/statistiques`.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar, PenTool, BarChart3, MessageSquare, MessagesSquare,
  FileText, UserCircle, AlertTriangle, Loader2, Plus,
  Newspaper, LogOut, ShieldCheck, Sun, Moon,
  Heart, TrendingUp,
} from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '../../store';
import { logout as logoutAction } from '../../store/slices/authSlice';
import {
  useFormateurMeQuery, useMonPlanningQuery, useActualitesFilQuery,
} from '../../services/formateurApi';
import {
  CarteActualite, ModalCommentaires, ModalPublier,
} from '../components/CompoFil';
import { useFormateurTheme } from '../useTheme';

interface Raccourci {
  icon: any; label: string; path: string; badge?: number;
  accent?: 'blue' | 'emerald' | 'amber' | 'indigo' | 'rose' | 'violet' | 'sky';
}

// Palette accent par raccourci (pastilles colorées derrière l'icône)
const ACCENT_CLS: Record<string, string> = {
  blue:    'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300',
  emerald: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300',
  amber:   'bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-300',
  indigo:  'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-300',
  rose:    'bg-rose-50 text-rose-600 dark:bg-rose-900/30 dark:text-rose-300',
  violet:  'bg-violet-50 text-violet-600 dark:bg-violet-900/30 dark:text-violet-300',
  sky:     'bg-sky-50 text-sky-600 dark:bg-sky-900/30 dark:text-sky-300',
};

export default function Accueil() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data, isLoading } = useFormateurMeQuery();
  const { data: planning } = useMonPlanningQuery();
  const { data: fil, isLoading: filLoading } = useActualitesFilQuery();
  const { isLight, toggle } = useFormateurTheme();

  const user = useSelector((s: RootState) => s.auth.user) as any;
  const photoUrl = user?.photo_url || null;

  const [showCompose, setShowCompose] = useState(false);
  const [activeCommentId, setActiveCommentId] = useState<number | null>(null);
  const [showCSClasses, setShowCSClasses] = useState<boolean>(() => {
    try { return localStorage.getItem('formateur_show_cs') === '1'; } catch { return false; }
  });

  const handleLogout = () => {
    if (!window.confirm('Se déconnecter ?')) return;
    dispatch(logoutAction());
    navigate('/login');
  };

  // 5 raccourcis Enseignement (avec Statistiques comme nouvelle rubrique).
  const enseignement: Raccourci[] = [
    { icon: PenTool,    label: 'Notes',        path: '/formateur/saisie-notes',  accent: 'rose' },
    { icon: Calendar,   label: 'Planning',     path: '/formateur/planning',      accent: 'amber' },
    { icon: BarChart3,  label: 'Présences',    path: '/formateur/presences',     accent: 'emerald' },
    { icon: Calendar,   label: 'Examens',      path: '/formateur/mes-examens',   accent: 'blue' },
    { icon: TrendingUp, label: 'Statistiques', path: '/formateur/statistiques',  accent: 'indigo' },
  ];
  const communication: Raccourci[] = [
    { icon: MessageSquare,  label: 'Étudiants', path: '/formateur/messages',
      badge: data?.kpi.nb_messages_non_lus, accent: 'blue' },
    { icon: MessageSquare,  label: 'Parents',   path: '/formateur/messages-parents', accent: 'sky' },
    { icon: MessagesSquare, label: 'Interne',   path: '/formateur/messagerie-interne', accent: 'violet' },
    { icon: Heart,          label: 'Conduite',  path: '/formateur/conduite', accent: 'rose' },
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-gray-950 text-slate-900 dark:text-gray-100 transition-colors">
      {/* Header sticky — sobre */}
      <header className="sticky top-0 z-30 bg-white dark:bg-gray-900 border-b border-slate-200 dark:border-gray-800">
        <div className="max-w-2xl mx-auto px-3 py-2 flex items-center gap-2.5">
          <button onClick={() => navigate('/formateur/profil')}
            className="flex items-center gap-2 flex-1 min-w-0 hover:opacity-90 transition">
            {photoUrl ? (
              <img src={photoUrl} alt="Profil"
                className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-slate-200 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
                <UserCircle size={22} className="text-slate-500 dark:text-gray-500" />
              </div>
            )}
            <div className="flex-1 min-w-0 text-left">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-gray-500 font-semibold leading-none">
                Espace formateur
              </div>
              <div className="text-sm font-bold text-slate-900 dark:text-white truncate leading-tight">
                {data?.formateur.nom_complet || '…'}
              </div>
            </div>
          </button>
          <button onClick={toggle}
            className="p-1.5 text-slate-500 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-gray-800 rounded transition"
            title={isLight ? 'Mode sombre' : 'Mode clair'}>
            {isLight ? <Moon size={16} /> : <Sun size={16} />}
          </button>
          <button onClick={handleLogout}
            className="p-1.5 text-slate-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-slate-100 dark:hover:bg-gray-800 rounded transition"
            title="Déconnexion">
            <LogOut size={16} />
          </button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-3 py-3 pb-24 space-y-3">

        {/* Alerte prochain cours imminent */}
        {planning?.alerte_imminente && planning?.prochain_cours && (
          <button onClick={() => navigate('/formateur/planning')}
            className="w-full bg-red-600 dark:bg-red-900/40 border border-red-700 dark:border-red-800 text-white dark:text-red-100 rounded-lg p-3 text-left">
            <div className="flex items-start gap-2">
              <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm">
                  Cours dans {Math.floor(planning.prochain_cours.dans_minutes / 60)}h
                  {String(planning.prochain_cours.dans_minutes % 60).padStart(2, '0')}min
                </div>
                <div className="text-xs mt-0.5 truncate opacity-90">
                  {planning.prochain_cours.matiere} · {planning.prochain_cours.classe}
                  {' · '}{planning.prochain_cours.heure_debut}–{planning.prochain_cours.heure_fin}
                </div>
              </div>
            </div>
          </button>
        )}

        {/* Composer rapide */}
        <div className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-lg p-2.5 flex items-center gap-2.5">
          {photoUrl ? (
            <img src={photoUrl} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
          ) : (
            <div className="w-9 h-9 rounded-full bg-slate-200 dark:bg-gray-800 flex items-center justify-center text-slate-500 dark:text-gray-500 flex-shrink-0">
              <UserCircle size={22} />
            </div>
          )}
          <button onClick={() => setShowCompose(true)}
            className="flex-1 text-left px-3 py-2 bg-slate-100 dark:bg-gray-800/60 hover:bg-slate-200 dark:hover:bg-gray-800 text-slate-500 dark:text-gray-400 text-sm rounded-full transition">
            Quoi de neuf, {(data?.formateur.nom_complet || '').split(' ')[0] || 'professeur'} ?
          </button>
          <button onClick={() => setShowCompose(true)}
            className="p-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white flex-shrink-0 transition"
            title="Publier">
            <Plus size={14} />
          </button>
        </div>

        {/* ── RACCOURCIS EN HAUT — Enseignement (5 boutons) ── */}
        <section>
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 dark:text-gray-500 px-1 mb-1.5">
            Enseignement
          </div>
          <div className="grid grid-cols-5 gap-2">
            {enseignement.map((r, i) => (
              <button key={i} onClick={() => navigate(r.path)}
                className="bg-white dark:bg-gray-900 hover:bg-slate-50 dark:hover:bg-gray-800 active:scale-95 border border-slate-200 dark:border-gray-800 rounded-lg py-2.5 px-1 flex flex-col items-center gap-1 transition relative">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                  r.accent ? ACCENT_CLS[r.accent] : 'bg-slate-100 dark:bg-gray-800 text-slate-700 dark:text-gray-300'
                }`}>
                  <r.icon size={17} />
                </div>
                <span className="text-[10px] font-medium text-slate-700 dark:text-gray-200 truncate w-full text-center px-0.5">
                  {r.label}
                </span>
                {!!r.badge && r.badge > 0 && (
                  <span className="absolute top-1.5 right-1.5 min-w-[16px] h-[16px] text-[9px] font-bold bg-red-500 text-white rounded-full px-1 flex items-center justify-center">
                    {r.badge > 99 ? '99+' : r.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </section>

        {/* ── Communication ── */}
        <section>
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 dark:text-gray-500 px-1 mb-1.5">
            Communication
          </div>
          <div className="grid grid-cols-4 gap-2">
            {communication.map((r, i) => (
              <button key={i} onClick={() => navigate(r.path)}
                className="bg-white dark:bg-gray-900 hover:bg-slate-50 dark:hover:bg-gray-800 active:scale-95 border border-slate-200 dark:border-gray-800 rounded-lg py-2.5 px-1 flex flex-col items-center gap-1 transition relative">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                  r.accent ? ACCENT_CLS[r.accent] : 'bg-slate-100 dark:bg-gray-800 text-slate-700 dark:text-gray-300'
                }`}>
                  <r.icon size={17} />
                </div>
                <span className="text-[11px] font-medium text-slate-700 dark:text-gray-200 truncate w-full text-center px-0.5">
                  {r.label}
                </span>
                {!!r.badge && r.badge > 0 && (
                  <span className="absolute top-1.5 right-1.5 min-w-[16px] h-[16px] text-[9px] font-bold bg-red-500 text-white rounded-full px-1 flex items-center justify-center">
                    {r.badge > 99 ? '99+' : r.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </section>

        {/* ── Ressources ── */}
        <section>
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 dark:text-gray-500 px-1 mb-1.5">
            Ressources
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => navigate('/formateur/supports')}
              className="bg-white dark:bg-gray-900 hover:bg-slate-50 dark:hover:bg-gray-800 active:scale-[0.98] border border-slate-200 dark:border-gray-800 rounded-lg p-2.5 flex items-center gap-2.5 text-left transition">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${ACCENT_CLS.blue}`}>
                <FileText size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-900 dark:text-white truncate">Supports</div>
                <div className="text-[10px] text-slate-500 dark:text-gray-400 truncate">Documents & cours</div>
              </div>
            </button>
            <button onClick={() => navigate('/formateur/depot-sujets')}
              className="bg-white dark:bg-gray-900 hover:bg-slate-50 dark:hover:bg-gray-800 active:scale-[0.98] border border-slate-200 dark:border-gray-800 rounded-lg p-2.5 flex items-center gap-2.5 text-left transition">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${ACCENT_CLS.amber}`}>
                <ShieldCheck size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-900 dark:text-white truncate">Dépôt sujets</div>
                <div className="text-[10px] text-slate-500 dark:text-gray-400 truncate">Confidentiel</div>
              </div>
            </button>
          </div>
        </section>

        {/* Mes classes (chips) */}
        {(data?.classes?.length ?? 0) > 0 && (() => {
          const classesAll = data!.classes;
          const classesCS = classesAll.filter(c => c.code.endsWith('-CS'));
          const classesFI = classesAll.filter(c => !c.code.endsWith('-CS'));
          const classesAffichees = showCSClasses ? classesAll : classesFI;
          return (
            <div className="flex flex-wrap gap-1.5 px-1 items-center pt-1">
              <span className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-gray-500 font-semibold mr-1">
                Mes classes
              </span>
              {classesAffichees.map(c => (
                <span key={c.code}
                  className={`px-2 py-0.5 text-[10px] font-semibold rounded border ${
                    c.code.endsWith('-CS')
                      ? 'bg-slate-100 dark:bg-gray-800 text-slate-700 dark:text-gray-300 border-slate-300 dark:border-gray-700'
                      : 'bg-white dark:bg-gray-900 text-slate-700 dark:text-gray-300 border-slate-200 dark:border-gray-700'
                  }`}>
                  {c.code}
                </span>
              ))}
              {classesCS.length > 0 && (
                <button
                  onClick={() => {
                    const next = !showCSClasses;
                    setShowCSClasses(next);
                    try { localStorage.setItem('formateur_show_cs', next ? '1' : '0'); } catch {}
                  }}
                  className="ml-1 px-2 py-0.5 text-[10px] font-medium rounded border border-slate-300 dark:border-gray-700 text-slate-600 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-gray-800"
                >
                  {showCSClasses ? '× Masquer CS' : `+ ${classesCS.length} CS`}
                </button>
              )}
            </div>
          );
        })()}

        {/* ── FIL D'ACTUALITÉS (publications récentes en haut du fil, tri natif backend) ── */}
        <section className="pt-1">
          <div className="flex items-center justify-between mb-2 px-1">
            <h2 className="text-[11px] uppercase tracking-wider font-bold text-slate-600 dark:text-gray-400 inline-flex items-center gap-1.5">
              <Newspaper size={11} /> Fil d'actualités
            </h2>
            <span className="text-[10px] text-slate-400 dark:text-gray-600">
              {fil?.total || 0} publication{(fil?.total || 0) > 1 ? 's' : ''}
            </span>
          </div>

          {(filLoading || isLoading) && (
            <div className="text-center py-6 text-slate-500 dark:text-gray-500 text-sm">
              <Loader2 size={14} className="inline animate-spin mr-2" /> Chargement…
            </div>
          )}

          <div className="space-y-3">
            {(fil?.results || []).map(a => (
              <CarteActualite key={a.id} a={a}
                onOpenComments={() => setActiveCommentId(a.id)} />
            ))}
            {!filLoading && (fil?.results || []).length === 0 && (
              <div className="bg-white dark:bg-gray-900 border border-dashed border-slate-300 dark:border-gray-800 rounded-lg p-8 text-center text-slate-500 dark:text-gray-500 text-sm">
                <Newspaper size={28} className="inline mb-2 opacity-40" />
                <div>Aucune actualité pour l'instant.</div>
                <div className="text-[11px] mt-1">Soyez le premier à publier.</div>
              </div>
            )}
          </div>
        </section>
      </main>

      {showCompose && (
        <ModalPublier onClose={() => setShowCompose(false)} />
      )}
      {activeCommentId !== null && (
        <ModalCommentaires actualiteId={activeCommentId}
          onClose={() => setActiveCommentId(null)} />
      )}
    </div>
  );
}
